import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("agent");

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/portal"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const u = await login(email, pass, mode);
      nav(u.role === "admin" ? "/admin" : "/portal");
    } catch (err) {
      setError(errMsg(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-[#0B0F17]">
      <div className="hidden lg:flex flex-col justify-between p-12 grid-bg border-r border-slate-800 relative overflow-hidden">
        <div className="font-display text-xl font-semibold">Residual<span className="text-emerald-400">Ledger</span></div>
        <div className="max-w-lg fade-up">
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-emerald-400 mb-5">Monthly residual reporting</div>
          <h1 className="text-5xl font-bold tracking-tight leading-[1.05]">Every merchant.<br />Every split.<br /><span className="text-slate-500">Reconciled.</span></h1>
          <p className="mt-6 text-slate-400 leading-relaxed">Upload processor statements, map the columns once, and every agent sees exactly what they earned — merchant by merchant.</p>
        </div>
        <div className="grid grid-cols-3 gap-4 font-mono text-xs text-slate-500">
          <div><div className="text-slate-200 text-lg">.xlsx</div>Excel & CSV</div>
          <div><div className="text-slate-200 text-lg">%</div>Per-agent splits</div>
          <div><div className="text-slate-200 text-lg">MoM</div>Monthly history</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <h2 className="text-2xl font-semibold tracking-tight">Sign in</h2>
          <div className="grid grid-cols-2 gap-1 p-1 mt-5 rounded-md bg-[#131B2E] border border-slate-800" role="tablist">
            {[["agent", "Agent login"], ["admin", "Admin login"]].map(([k, label]) => (
              <button key={k} type="button" role="tab" data-testid={`login-tab-${k}`} onClick={() => { setMode(k); setError(""); }}
                className={`h-9 rounded text-sm transition-colors ${mode === k ? (k === "admin" ? "bg-emerald-500/15 text-emerald-300" : "bg-blue-500/15 text-blue-300") : "text-slate-400 hover:text-slate-200"}`}>{label}</button>
            ))}
          </div>
          <p className="text-sm text-slate-400 mt-3 mb-8" data-testid="login-mode-hint">{mode === "admin" ? "For the account owner — upload reports, manage agents and payouts." : "Agents: sign in with the email and passcode your admin gave you."}</p>
          <label className="text-xs font-mono uppercase tracking-wider text-slate-500">Email</label>
          <Input data-testid="email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 mb-5 h-11 bg-[#131B2E]" placeholder="you@company.com" />
          <label className="text-xs font-mono uppercase tracking-wider text-slate-500">Passcode</label>
          <Input data-testid="passcode-input" type="password" required value={pass} onChange={(e) => setPass(e.target.value)} className="mt-2 h-11 bg-[#131B2E]" placeholder="••••••" />
          {error && <div data-testid="login-error" className="mt-4 text-sm text-rose-400">{error}</div>}
          <Link to="/forgot-passcode" data-testid="forgot-passcode-link" className="block mt-4 text-xs text-slate-400 hover:text-blue-300">Forgot passcode?</Link>
          <Button data-testid="login-submit-button" disabled={busy} className="w-full h-11 mt-8 bg-blue-500 hover:bg-blue-600 text-white">
            {busy ? "Signing in…" : mode === "admin" ? "Sign in as admin" : "Sign in as agent"}
          </Button>
        </form>
      </div>
    </div>
  );
}
